import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import type { ComponentProps } from 'react'
import { useCallback } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import LuxPlusBookingProtectionLabelLabel from 'luxPlus/components/LuxPlusBookingProtectionLabel'
import LuxPlusInsuranceLabel from 'luxPlus/components/LuxPlusInsuranceLabel'
import _BaseLabel from './_BaseLabel'
import styled from 'styled-components'

const LOGO_HEIGHT = 10

const LuxPlusLabelElement = styled(_BaseLabel)`
  background-color: var(--palette-product-lux-plus-background);
  border-color: var(--palette-product-lux-plus-foreground);
  color: var(--palette-product-lux-plus-contrast);
`

interface Props extends Utils.PropsWithDataAttributes {
  type: App.LuxPlusLabelType
  saveAmount?: number
  hideSaveAmountPlus?: boolean
  /** Hide the visual info icon without changing the label's click behaviour. */
  hideInfoIcon?: boolean
  inclusionDescription?: string
  onClick?: () => void
  to?: ComponentProps<typeof Clickable>['to']
}

function LuxPlusLabel(props: Props) {
  const {
    inclusionDescription,
    type,
    onClick,
    saveAmount,
    hideSaveAmountPlus,
    hideInfoIcon = false,
    to,
    ...rest
  } = props

  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const clickHandler = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return
    e.preventDefault()
    e.stopPropagation()
    onClick()
  }, [onClick])

  if (!luxPlusEnabled) return null

  const saveAmountSuffix = hideSaveAmountPlus ? '' : '+'

  const label = <LuxPlusLabelElement
    data-testid="lux-plus-label"
    {...rest}
    endIcon={onClick && !hideInfoIcon ? <LineInfoCircleIcon /> : undefined}
  >
    {type === 'bonus-inclusions' && !inclusionDescription && <FormattedMessage
      id="OXS7m77_WNp7-9yj1N1H"
      defaultMessage="Bonus inclusions with {logo}"
      
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'bonus-inclusions' && !!inclusionDescription && <FormattedMessage
      id="31PC5vk6YX2bPgu1HXtd"
      defaultMessage="{description} with {logo}"
      
      values={{ description: inclusionDescription, logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member' && <FormattedMessage
      id="LqeGDlONUe7cK3RCwLAe"
      defaultMessage="{logo} member"
      
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'only-logo' && <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />}
    {type === 'early-access-offer' && <FormattedMessage
      id="SmkL8grGGWz9G77r3j4T"
      defaultMessage="Early access with {logo}"
      
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-only-offer' && <FormattedMessage
      id="JrNCTRM29GVguPsz02zY"
      defaultMessage="{logo} member-only offer"
      
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-only-discount' && <FormattedMessage
      id="MyI_Co-dMTx3MsUDzTp5"
      defaultMessage="{logo} member-only discount"
      
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-price-offer' && !!saveAmount && <FormattedMessage
      id="yG8-sK8jiKIX-_Lf8fns"
      defaultMessage="Save {formattedSaveAmount} with {logo}"
      
      values={{
        formattedSaveAmount: <><FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}</>,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'member-price-offer-cruise' && <FormattedMessage
      id="njgIffwG8jGiVQtUVt72"
      defaultMessage="{logo} discount available"
      
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-price-offer-flight' && <FormattedMessage
      id="ygiLnkQWWpD1EHQZQiz-"
      defaultMessage="{logo} member discount available"
      
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-price-offer-lpp' && !!saveAmount && <FormattedMessage
      id="ZgpitutO8uJvxV_WnBLA"
      defaultMessage="Save {formattedSaveAmount} with {logo}"
      
      values={{
        formattedSaveAmount: <><FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}</>,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'member-price-offer-tour' && !!saveAmount && <FormattedMessage
      id="5zSuQiiJi_1-DaMaQRsZ"
      defaultMessage="Save {formattedSaveAmount}/person with {logo}"
      
      values={{
        formattedSaveAmount: <><FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}</>,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'options-available-tour' && <FormattedMessage
      id="XAJJIOTUNBekdLVgbPGO"
      defaultMessage="{logo} options available"
      
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'plus' && <LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} />}
    {type === 'plus-preview' && <FormattedMessage
      id="CoLeUQmp9AUbIvMIEFnn"
      defaultMessage="{logo} preview"
      
      values={{ logo: <LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'insurance' && !!saveAmount && <LuxPlusInsuranceLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
    {type === 'booking-protection' && !!saveAmount && <LuxPlusBookingProtectionLabelLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
    {type === 'savings' && !!saveAmount && <FormattedMessage
      id="3aEGrp8JsLihhAF0oo1P"
      defaultMessage="Extra {formattedSaveAmount} saved with {logo}"
      
      values={{
        formattedSaveAmount: <FormatCurrency format="roundedDollar" value={saveAmount} />,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'member-inclusions' && <FormattedMessage
      id="6zoPC0xA1-A1hjHpW0sI"
      defaultMessage="Member inclusions with {logo}"
      
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
  </LuxPlusLabelElement>

  if (onClick || to) return <Clickable onClick={clickHandler} to={to} data-testid="lux-plus-label-clickable">{label}</Clickable>

  return label
}

export default LuxPlusLabel
